import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Buttons from "../../../components/Controls/Buttons/Button";

const HistoricalBatchMetadataView = ({ data, versionNumber, onBack }) => {
  const sheets = useMemo(
    () =>
      [...(data?.sheets || [])].sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0),
      ),
    [data],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [data?.batch_id, data?.old_ingested_file_id]);

  const selectedSheet = sheets[selectedIndex];
  const rows = selectedSheet?.rows?.length
    ? selectedSheet.rows
    : selectedSheet?.columns?.length
      ? [
        {
          sheet_row_id: "preview-row",
          values: selectedSheet.columns.map((column) => ({
            ...column,
            value_text: column.preview_value ?? column.old_value,
          })),
        },
      ]
      : [];
  const columns = (selectedSheet?.columns || []).map((column, index) =>
    typeof column === "string"
      ? {
        column_id: `${selectedSheet?.sheet_id}-${index}`,
        column_name: column,
      }
      : column,
  );
  const displayColumns = columns.length ? columns : rows[0]?.values || [];

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ borderBottom: "1px solid #E4E7EC", px: 1, py: 1 }}
      >
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            minWidth: 70,
            borderRadius: 5,
            bgcolor: "#F2F4F7",
            color: "#344054",
            textTransform: "none",
          }}
        >
          Back
        </Button>
        <Typography fontSize={14} color="#667085">
          Copied Batches
        </Typography>
        <Typography fontSize={14} color="#98A2B3">
          ›
        </Typography>
        <Typography fontSize={14} color="#175CD3" fontWeight={600}>
          View Batch
        </Typography>
      </Box>

      <Box display="flex" gap={1} mt={1} height="calc(100vh - 175px)">
        <Box
          sx={{
            width: 270,
            flexShrink: 0,
            bgcolor: "#E8EEF7",
            borderRadius: 1,
            p: 1,
            overflowY: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={0.5}
            mb={1}

          >
            <Typography fontSize={14} fontWeight={700}>
              Uploaded Data
            </Typography>
            <Chip
              size="small"
              label={versionNumber}
              sx={{ bgcolor: "#DDEEFF", color: "#175CD3", fontSize: 14 }}
            />
          </Box>
          {sheets.map((sheet, index) => {
            const selected = index === selectedIndex;
            return (
              <Button
                key={sheet.sheet_id}
                fullWidth
                onClick={() => setSelectedIndex(index)}
                // endIcon={<MoreHorizIcon />}
                sx={{
                  justifyContent: "space-between",
                  textTransform: "none",
                  fontSize: 14,
                  color: selected ? "#FFFFFF" : "#344054",
                  bgcolor: selected ? "#175A9E" : "#FFFFFF",
                  borderRadius: 1,
                  mb: 0.75,
                  px: 1.25,
                  py: 1,
                  "& .MuiButton-endIcon": { color: "inherit" },
                  "&:hover": { bgcolor: selected ? "#175A9E" : "#F8FAFC" },
                }}
              >
                <Typography fontSize={14} color="inherit" noWrap>
                  {sheet.name || sheet.sheet_name}
                </Typography>
              </Button>
            );
          })}
        </Box>

        <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: "none", flex: 1, overflow: "auto" }}
          >
            <Table
              stickyHeader
              sx={{ minWidth: Math.max(700, displayColumns.length * 180) }}
            >
              <TableHead>
                <TableRow>
                  {displayColumns.map((column, index) => (
                    <TableCell
                      key={column.column_id}
                      sx={{
                        bgcolor: "#E9EAEB",
                        color: "#101828",
                        fontSize: 14,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        py: 1.25,
                        ...(index === 0 && { borderRadius: "8px 0 0 0" }),
                        ...(index === displayColumns.length - 1 && {
                          borderRadius: "0 8px 0 0",
                        }),
                      }}
                    >
                      {column.column_name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => {
                  const valuesByName = new Map(
                    (row.values || []).map((value) => [
                      String(value.column_name || "")
                        .trim()
                        .toLowerCase(),
                      value,
                    ]),
                  );
                  return (
                    <TableRow key={row.sheet_row_id || row.row_id || rowIndex}>
                      {displayColumns.map((column) => {
                        const columnName = String(column.column_name || "");
                        const isStatus =
                          columnName.trim().toLowerCase() === "status";
                        const value = valuesByName.get(
                          columnName.trim().toLowerCase(),
                        );
                        return (
                          <TableCell
                            key={`${row.sheet_row_id || rowIndex}-${column.column_id || columnName}`}
                            title={value?.change_details || ""}
                            sx={{
                              fontSize: 14,
                              py: 1.25,
                            }}
                          >
                            {isStatus
                              ? row.status || "-"
                              : (value?.value_text ??
                                value?.preview_value ??
                                value?.old_value ??
                                "-")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {!displayColumns.length && (
                  <TableRow>
                    <TableCell align="center">
                      <Typography fontSize={14}>No metadata found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" gap={1} p={1.5}>
            {/* <Button
              variant="outlined"
              disabled={selectedIndex === 0}
              onClick={() => setSelectedIndex((index) => index - 1)}
              sx={{
                textTransform: "none",
                "&.Mui-disabled": {
                  color: "#98A2B3",
                  borderColor: "#D0D5DD",
                  bgcolor: "#FFFFFF",
                  opacity: 1,
                },
              }}
            >
              Previous
            </Button> */}
            <Buttons
              text={"Previous"}
              disabled={selectedIndex === 0}
              onClick={() => setSelectedIndex((index) => index - 1)}
            />
            <Buttons
              text={"Next"}
              disabled={selectedIndex >= sheets.length - 1}
              onClick={() => setSelectedIndex((index) => index + 1)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HistoricalBatchMetadataView;
